/* QUALITY-1 (V21, 24 Oct 2026, top 100 item 36, WCAG 2.2 AA): the keyboard behaviour of the panels that open over a page.
   Measured on the live shop with a keyboard walk (site-changes/2026-10-24-quality1/tools/q1_dialogs.mjs): the menu drawer of the phone header is a dialog (role=dialog, aria-modal) but the focus stayed on the
   button behind it, Tab walked out into the page under the overlay, and Escape did not give the focus back; the menus of the desktop header open without the focus going in, Tab went on to the next
   menu button, and the search suggestions stayed open over the links below the search box while the keyboard went on (a focused link hidden under them: 2.4.11 Focus Not Obscured).
   Rules (WAI-ARIA authoring practice), done for every modal dialog of the theme at once and for the header's menu panels:
     1. a MODAL dialog that opens takes the focus (its first control), keeps Tab and Shift+Tab inside it, and gives the focus back to the control that opened it when it closes;
     2. a menu panel opened with the KEYBOARD (Enter or Space on its button) takes the focus into its first link; Shift+Tab from that link goes back to the button; Escape closes it (the header's own handler)
        and the focus goes back to the button; when the focus leaves a keyboard-opened panel the panel closes, so it never covers the page under the focus;
     3. the search suggestions close when the keyboard moves on (a mouse click on a suggestion is untouched).
   It reads and writes nothing but the focus and the suggestion box's hidden flag. No visible change, no new words.
   Guards (24 Oct 2026, after the first version was on LIVE): a dialog is managed only when it opened within 5 s of a user gesture (a popup that opens by itself, on a timer or an exit, is left to its own script: it
   must never take the focus from a visitor who is typing); the dialogs that have their own focus code (#lc the library-card popup, #su the sign-up, #dl the download sheet) are left alone; when the first control
   of a dialog is a text field the container takes the focus instead (the phone's keyboard must not open by itself). */
(() => {
  const FOC = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';
  const shown = el => {
    for (let e = el; e && e !== document.documentElement; e = e.parentElement) {
      if (e.hidden) return false;
      const s = getComputedStyle(e);
      if (s.display === 'none' || s.visibility === 'hidden') return false;
    }
    return true;
  };
  const focusables = root => [...root.querySelectorAll(FOC)].filter(e => e.getClientRects().length && getComputedStyle(e).visibility !== 'hidden' && !e.closest('[hidden],[inert]'));
  const DIALOGS_SEL = '[role="dialog"][aria-modal="true"]';
  let kbd = false, opener = null, gesture = 0;
  const SELF = '#lc,#su,#dl';   // dialogs with focus code of their own
  ['pointerdown', 'keydown', 'touchstart'].forEach(t => document.addEventListener(t, () => { gesture = Date.now(); }, true));
  document.addEventListener('keydown', e => { if (e.key !== 'Escape') kbd = true; }, true);
  document.addEventListener('pointerdown', e => { kbd = false; opener = e.target.closest ? e.target.closest(FOC) || opener : opener; }, true);
  document.addEventListener('focusin', e => { if (!e.target.closest || !e.target.closest(DIALOGS_SEL)) opener = e.target; }, true);

  /* 1. modal dialogs */
  const dialogs = new Map();   // dialog -> { open, opener }
  const open = () => [...dialogs].filter(([, s]) => s.open && s.managed).map(([d]) => d);
  /* open = displayed, not aria-hidden and inside the screen (a panel that is only slid out of sight, or still sliding in, is looked at again a moment later) */
  const isOpen = d => {
    if (!shown(d) || d.closest('[aria-hidden="true"]')) return false;
    const r = d.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.right > 0 && r.left < innerWidth && r.bottom > 0 && r.top < innerHeight;
  };
  const sync = d => {
    const st = dialogs.get(d), now = isOpen(d);
    if (now && !st.open) {
      st.open = true;
      st.managed = Date.now() - gesture < 5000;
      if (!st.managed) return;
      st.opener = opener && !d.contains(opener) ? opener : (document.activeElement && !d.contains(document.activeElement) ? document.activeElement : null);
      requestAnimationFrame(() => {
        if (!st.open || d.contains(document.activeElement)) return;
        const first = d.querySelector('[autofocus]') || focusables(d)[0];
        const text = first && first.matches('textarea,input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]):not([type=range]):not([type=hidden])');
        if (first && !text) first.focus({ preventScroll: true });
        else { d.setAttribute('tabindex', '-1'); d.focus({ preventScroll: true }); }
      });
    } else if (!now && st.open) {
      const was = st.managed;
      st.open = false; st.managed = false;
      const a = document.activeElement;
      if (was && (!a || a === document.body || d.contains(a)) && st.opener && st.opener.isConnected && shown(st.opener)) st.opener.focus({ preventScroll: true });
    }
  };
  const watch = d => {
    if (dialogs.has(d) || d.matches(SELF)) return;
    dialogs.set(d, { open: false, managed: false, opener: null });
    const mo = new MutationObserver(() => [0, 150, 400, 800].forEach(ms => setTimeout(() => sync(d), ms)));
    for (let e = d; e && e !== document.body; e = e.parentElement) mo.observe(e, { attributes: true, attributeFilter: ['hidden', 'style', 'class', 'open'] });
    sync(d);
  };
  const scan = () => document.querySelectorAll(DIALOGS_SEL).forEach(watch);
  scan();
  new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('keydown', e => {
    if (e.key !== 'Tab') return;
    const d = open().pop();
    if (!d) return;
    const f = focusables(d);
    if (!f.length) { e.preventDefault(); return; }
    const a = document.activeElement;
    if (!d.contains(a)) { e.preventDefault(); f[0].focus(); return; }
    if (e.shiftKey && a === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
    else if (!e.shiftKey && a === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
  }, true);

  /* 2. the header's menu panels (not the drawer, not the search panel) */
  const h = document.querySelector('[data-h16]');
  if (!h) return;
  const menus = [...document.querySelectorAll('button[aria-controls]')]
    .map(b => [b, document.getElementById(b.getAttribute('aria-controls'))])
    .filter(([b, p]) => p && p.classList.contains('m16') && !p.classList.contains('srch16') && h.contains(b));
  menus.forEach(([b, p]) => {
    let byKey = false;
    new MutationObserver(() => {
      if (!p.hidden) {
        byKey = kbd;
        if (byKey) requestAnimationFrame(() => { const f = focusables(p)[0]; if (f && !p.hidden) f.focus({ preventScroll: true }); });
      } else {
        const a = document.activeElement;
        if (byKey && (!a || a === document.body || p.contains(a))) b.focus({ preventScroll: true });
        byKey = false;
      }
    }).observe(p, { attributes: true, attributeFilter: ['hidden'] });
    p.addEventListener('keydown', e => {
      if (e.key === 'Tab' && e.shiftKey && document.activeElement === focusables(p)[0]) { e.preventDefault(); b.focus(); }
    });
    document.addEventListener('focusin', e => {
      if (!byKey || p.hidden || p.contains(e.target) || b.contains(e.target)) return;
      byKey = false;
      b.click();   // the header's own toggle closes a panel that is open
    });
  });

  /* 3. the search suggestions close when the keyboard moves on */
  h.querySelectorAll('form[role="search"]').forEach(form => {
    form.addEventListener('focusout', e => {
      if (!kbd || (e.relatedTarget && form.contains(e.relatedTarget))) return;
      const box = form.querySelector('.h16-sug');
      if (box && !box.hidden && !form.contains(document.activeElement)) box.hidden = true;
    });
  });
  document.querySelectorAll('form.srch16').forEach(form => form.addEventListener('focusout', () => {
    setTimeout(() => { const box = form.querySelector('.h16-sug'); if (kbd && box && !box.hidden && !form.contains(document.activeElement)) box.hidden = true; }, 0);
  }));
})();
